import { Fragment, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Container, Input } from "reactstrap";
import { getReservations, fetchBranches } from "../../utils/apiCalls";
import { C, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, Loader } from "../reporting/ReportingShared";
import Breadcrumb from "../common/breadcrumb";

const statusColors = {
  pending: C.orange, confirmed: C.blue, seated: C.purple,
  completed: C.green, cancelled: C.textLight, no_show: C.coral,
};

const HOURS = Array.from({ length: 16 }, (_, i) => i + 8);
const today = () => new Date().toISOString().split("T")[0];

const ReservationCalendar = () => {
  const navigate = useNavigate();
  const userType = localStorage.getItem("userType");
  const merchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchant = userType === "merchant" ? merchantId : reduxSelectedMerchant;

  const [branches, setBranches] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedDate, setSelectedDate] = useState(today());
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      const response = await fetchBranches({}, false);
      let list = response?.data || [];
      if (selectedMerchant && selectedMerchant !== "all") list = list.filter((b) => String(b.MerchantId) === String(selectedMerchant));
      setBranches(list);
    };
    load();
  }, [selectedMerchant]);

  const fetchData = useCallback(async () => {
    if (!selectedMerchant || selectedMerchant === "all") { setReservations([]); return; }
    setIsLoading(true);
    const params = { MerchantId: selectedMerchant, date: selectedDate, limit: 200 };
    if (selectedBranch) params.branchId = selectedBranch;
    const data = await getReservations(params);
    setReservations(data?.rows || []);
    setIsLoading(false);
  }, [selectedMerchant, selectedBranch, selectedDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const changeDate = (offset) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + offset);
    setSelectedDate(d.toISOString().split("T")[0]);
  };

  const timeToPosition = (timeStr) => {
    if (!timeStr) return 0;
    const [h, m] = timeStr.split(":").map(Number);
    return (h - 8) * 60 + m;
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  };

  const totalMinutes = HOURS.length * 60;

  return (
    <Fragment>
      <style>{reportingGlobalCSS}</style>
      <Breadcrumb title="Calendar" parent="Reservations" />
      <Container fluid className="rpt-wrap">
        {/* Filters */}
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 20, flexWrap: "wrap" }}>
          <Input type="select" value={selectedBranch} onChange={(e) => setSelectedBranch(e.target.value)} style={{ maxWidth: 200, borderRadius: 8, border: `1px solid ${C.border}` }}>
            <option value="">All Branches</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </Input>
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }}>
            <Button size="sm" style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMed, fontWeight: 600 }} onClick={() => changeDate(-1)}>
              <i className="fa fa-angle-left" />
            </Button>
            <span style={{ fontWeight: 700, fontSize: 15, color: C.text, minWidth: 260, textAlign: "center" }}>{formatDate(selectedDate)}</span>
            <Button size="sm" style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMed, fontWeight: 600 }} onClick={() => changeDate(1)}>
              <i className="fa fa-angle-right" />
            </Button>
            {selectedDate !== today() && (
              <Button size="sm" style={{ background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, border: "none", borderRadius: 8, fontWeight: 600, fontSize: 12 }} onClick={() => setSelectedDate(today())}>Today</Button>
            )}
          </div>
          <Button size="sm" style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMed, fontWeight: 600, fontSize: 12 }} onClick={() => navigate("/reservations/list")}>
            List View
          </Button>
        </div>

        {/* Calendar */}
        <RCard style={{ padding: 0, overflow: "hidden" }}>
          {isLoading ? <Loader /> : (!selectedMerchant || selectedMerchant === "all") ? (
            <p style={{ textAlign: "center", padding: 40, color: C.textLight }}>Select a merchant</p>
          ) : (
            <div style={{ position: "relative", minHeight: 200 }}>
              {/* Time header */}
              <div style={{ display: "flex", borderBottom: `2px solid ${C.border}`, position: "sticky", top: 0, background: `linear-gradient(180deg, #F8FAFC, #F1F5F9)`, zIndex: 2 }}>
                <div style={{ width: 80, flexShrink: 0, padding: "10px 8px", fontWeight: 700, fontSize: 11, color: C.textMed, textTransform: "uppercase", letterSpacing: 0.8 }}>TIME</div>
                <div style={{ flex: 1, display: "flex" }}>
                  {HOURS.map((h) => (
                    <div key={h} style={{
                      width: `${100 / HOURS.length}%`, textAlign: "center", padding: "10px 0",
                      fontSize: 11, fontWeight: 600, color: C.textMed, borderLeft: `1px solid ${C.borderLight}`,
                    }}>
                      {h > 12 ? `${h - 12} PM` : h === 12 ? "12 PM" : `${h} AM`}
                    </div>
                  ))}
                </div>
              </div>

              {/* Reservations */}
              {reservations.filter((r) => !["cancelled", "no_show"].includes(r.status)).length === 0 ? (
                <p style={{ textAlign: "center", padding: 40, color: C.textLight }}>No reservations for this date</p>
              ) : (
                reservations
                  .filter((r) => !["cancelled", "no_show"].includes(r.status))
                  .map((r) => {
                    const startPos = timeToPosition(r.startTime);
                    const duration = r.endTime ? timeToPosition(r.endTime) - startPos : 60;
                    const leftPercent = (startPos / totalMinutes) * 100;
                    const widthPercent = Math.max((duration / totalMinutes) * 100, 3);
                    const color = statusColors[r.status] || C.textMed;

                    return (
                      <div key={r.id} style={{ display: "flex", minHeight: 44, borderBottom: `1px solid ${C.borderLight}`, alignItems: "center" }} className="rpt-row">
                        <div style={{ width: 80, flexShrink: 0, padding: "4px 8px", fontSize: 12, fontWeight: 600, color: C.textMed }}>{r.startTime}</div>
                        <div style={{ flex: 1, position: "relative", height: 36 }}>
                          {HOURS.map((h) => (
                            <div key={h} style={{ position: "absolute", left: `${((h - 8) * 60 / totalMinutes) * 100}%`, top: 0, bottom: 0, width: 1, background: C.borderLight }} />
                          ))}
                          <div
                            onClick={() => navigate(`/reservations/edit/${r.id}`)}
                            style={{
                              position: "absolute", left: `${leftPercent}%`, width: `${widthPercent}%`,
                              top: 2, bottom: 2, background: color, borderRadius: 6, padding: "2px 8px",
                              cursor: "pointer", overflow: "hidden", whiteSpace: "nowrap",
                              display: "flex", alignItems: "center", gap: 6, boxShadow: `0 2px 8px ${color}40`,
                            }}
                          >
                            <span style={{ color: "#fff", fontSize: 11, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis" }}>{r.customerName}</span>
                            <span style={{ color: "rgba(255,255,255,0.8)", fontSize: 10 }}>({r.guestCount})</span>
                          </div>
                        </div>
                      </div>
                    );
                  })
              )}

              {/* Current time indicator */}
              {selectedDate === today() && (() => {
                const now = new Date();
                const currentMinutes = (now.getHours() - 8) * 60 + now.getMinutes();
                if (currentMinutes < 0 || currentMinutes > totalMinutes) return null;
                const leftPct = (currentMinutes / totalMinutes) * 100;
                return (
                  <div style={{
                    position: "absolute",
                    left: `calc(80px + (100% - 80px) * ${leftPct / 100})`,
                    top: 40, bottom: 0, width: 2, background: C.coral, zIndex: 1, pointerEvents: "none",
                  }}>
                    <div style={{ width: 8, height: 8, borderRadius: 4, background: C.coral, marginLeft: -3, marginTop: -4 }} />
                  </div>
                );
              })()}
            </div>
          )}
        </RCard>

        {/* Legend */}
        <div style={{ display: "flex", gap: 16, marginTop: 12, justifyContent: "center" }}>
          {Object.entries({ pending: "Pending", confirmed: "Confirmed", seated: "Seated", completed: "Completed" }).map(([key, label]) => (
            <div key={key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: C.textMed }}>
              <div style={{ width: 12, height: 12, borderRadius: 3, background: statusColors[key] }} />
              {label}
            </div>
          ))}
        </div>
      </Container>
    </Fragment>
  );
};

export default ReservationCalendar;
